import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Avatar, Box, Button, Grid, Typography, useTheme } from '@mui/material';
import moment from 'moment';
import { get } from 'lodash';
import { ArrowForward, RoomRounded } from '@mui/icons-material';
import routes from '../../../../routes';
import { ReactComponent as Wave1 } from '../../../../assets/idMode/wave1.svg';
import { ReactComponent as Wave2 } from '../../../../assets/idMode/wave2.svg';
import { ReactComponent as Hill1 } from '../../../../assets/idMode/hill1.svg';
import { ReactComponent as Hill2 } from '../../../../assets/idMode/hill2.svg';
import { hasRoleInCurrentlySelectedSite } from '../../../../services/auth';
import { Roles } from '../../../../models/roles';
import useActiveUser from '../../../../hooks/useActiveUser';
import { Colors } from '../../../../assets/theme/Colors';
import useActiveSite from '../../../../hooks/useActiveSite';
import frusLogo from '../../../../assets/images/new-frus-logo-oval.png';
import { getUserImage } from '../../../../helpers/user';
import { getTodayUnclosedRescues } from '../../../../helpers/RescuesHelper';

const customTextColorsTypes = {
  toolbar: 'toolbar',
  button: 'button',
}

const getCustomTextColor = (props, theme, defaultColor, type = customTextColorsTypes.toolbar) => {
  if (type === customTextColorsTypes.toolbar && props.customBranding?.toolbar_text_color && props.hasCustomBranding) {
    return props.customBranding.toolbar_text_color;
  }

  if (type === customTextColorsTypes.toolbar && props.customBranding?.primary_color && props.hasCustomBranding) {
    return theme.palette.getContrastText(props.customBranding.primary_color);
  }

  if (type === customTextColorsTypes.button  && props.customBranding?.button_text_color && props.hasCustomBranding) {
    return props.customBranding.button_text_color;
  }

  if (type === customTextColorsTypes.button  && props.customBranding?.secondary_color && props.hasCustomBranding) {
    return theme.palette.getContrastText(props.customBranding.secondary_color);
  }
  return theme.palette.getContrastText(defaultColor);
};

const IdModeMobileContainer = () => {
  const site = useActiveSite();
  const history = useHistory();
  const theme = useTheme();
  const location = useLocation();
  const activeUser = useActiveUser();
  const activeSite = useActiveSite();
  const sites = useSelector((state) => state.entities.sites.byId);
  const settings = useSelector((state) => state.entities.settings);
  const isRescuer = hasRoleInCurrentlySelectedSite(activeUser, Roles.Rescuer);
  const idTokenPayload = useSelector((state) => state.app.idTokenPayload);
  const picture = getUserImage(activeUser, idTokenPayload);
  const rescuesEntities = useSelector((state) => state.entities.rescues);
  const { state } = location;
  const todayUnclosedRescues = getTodayUnclosedRescues(activeUser.id, rescuesEntities);
  const currentRescues = todayUnclosedRescues.filter(
    (rescue) =>
      moment().isSameOrAfter(moment(rescue.pickup_begin, 'hh:mm')) &&
      moment().isSameOrBefore(moment(rescue.pickup_end, 'hh:mm'))
  );
  const currentRescue = currentRescues.length > 1 ? {} : currentRescues[0]; // show only if there is one current rescue
  const rescueId = state?.rescueId ? state.rescueId : currentRescue?.id;
  const rescue = get(rescuesEntities, ['byId', rescueId], {});

  // Check for custom branding setting
  const currentSite = Object.values(sites).find((s) => s.id === site?.id);
  const settingsList = Object.values(settings.byId);
  const customBrandingSetting = settingsList.find((setting) => setting.name === 'custom_branding');
  const hasCustomBranding =
    currentSite?.settings.find((setting) => setting.id === customBrandingSetting?.id)?.value === '1';
  const customBranding = {
    primary_color: currentSite?.primary_color,
    secondary_color: currentSite?.secondary_color,
    button_text_color: currentSite?.button_text_color,
    toolbar_text_color: currentSite?.toolbar_text_color,
  };

  const onExitClick = () => {
    history.push(routes.mobileDashboard);
  };

  if (!isRescuer) {
    return history.push(routes.index);
  }

  return (
    <Box
      sx={{
        backgroundColor:
          hasCustomBranding && customBranding.primary_color ? customBranding.primary_color : Colors.appBar.main,
        padding: 0,
        margin: 0,
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '100%',
      }}
    >
      <Wave1
        style={{
          position: 'absolute',
          width: '100%',
          right: -1,
          top: 0,
          zIndex: 2,
          fill:
            hasCustomBranding && customBranding.secondary_color ? customBranding.secondary_color : Colors.primaryColor,
        }}
      />
      <Wave2
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          fill:
            hasCustomBranding && customBranding.secondary_color ? customBranding.secondary_color : Colors.primaryColor,
          filter: 'brightness(3.8) saturate(0.2)',
        }}
      />
      <Hill1
        style={{
          position: 'absolute',
          bottom: 0,
          right: -1,
          zIndex: 2,
          fill:
            hasCustomBranding && customBranding.secondary_color ? customBranding.secondary_color : Colors.primaryColor,
      }} />
      <Hill2
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          fill: hasCustomBranding && customBranding.secondary_color ? customBranding.secondary_color : Colors.primaryColor,
          filter: 'brightness(3.8) saturate(0.2)',
      }} />
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '1rem',
          position: 'absolute',
          top: '35%',
          transform: 'translateY(-35%)',
          padding: '0 1rem',
          zIndex: 50,
        }}
      >
        <Avatar
          alt={activeUser?.firstname}
          src={picture}
          sx={{ height: '160px', width: '160px', fontSize: '4rem', border: '4px solid white' }}
        />
        <Typography
          variant="h3"
          sx={{
            textAlign: 'center',
            color: getCustomTextColor({ hasCustomBranding, customBranding }, theme, Colors.appBar.main),
          }}
        >{`${activeUser?.firstname} ${activeUser?.lastname}`}</Typography>
        <Button
          variant="contained"
          color="primary"
          disableRipple
          disableFocusRipple
          disableElevation
          fullWidth
          sx={{
            width: 'fit-content',
            borderRadius: '12px',
            padding: '0.5rem 3rem',
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 'normal',
            cursor: 'default',
            '&:hover': {
              backgroundColor:
                hasCustomBranding && customBranding.secondary_color
                  ? customBranding.secondary_color
                  : Colors.primaryColor,
            },
            backgroundColor:
              hasCustomBranding && customBranding.secondary_color
                ? customBranding.secondary_color
                : Colors.primaryColor,
          }}
        >
          Rescuer
        </Button>

        {rescue.location && rescue.receiver && (
          <Box mt={2}>
            <Grid container spacing={1}>
              <Grid item xs={5}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <RoomRounded sx={{ marginBottom: 1, color: 'primary.main', fontSize: '3rem' }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      color: getCustomTextColor({ hasCustomBranding, customBranding }, theme, Colors.appBar.main),
                    }}
                  >
                    {rescue.location}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={2}>
                <Box height="100%" display="flex" justifyContent="center" alignItems="start" mt={0.5}>
                  <ArrowForward
                    sx={{
                      fontSize: '2.5rem',
                      borderRadius: '50%',
                      backgroundColor: getCustomTextColor(
                        { hasCustomBranding, customBranding },
                        theme,
                        Colors.appBar.main
                      ),
                      color:
                        hasCustomBranding && customBranding.primary_color
                          ? customBranding.primary_color
                          : Colors.appBar.main,
                  }}/>
                </Box>
              </Grid>
              <Grid item xs={5}>
                <Box display="flex" flexDirection="column" alignItems="center">
                  <RoomRounded
                    sx={{ marginBottom: 1, color: 'primary.main', fontSize: '3rem' }}
                  />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      textAlign: 'center',
                      wordBreak: 'break-word',
                      color: getCustomTextColor({ hasCustomBranding, customBranding }, theme, Colors.appBar.main),
                    }}
                  >
                    {rescue.receiver}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box mt={2}>
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: 'center',
              color: getCustomTextColor({ hasCustomBranding, customBranding }, theme, Colors.appBar.main),
            }}
          >
            {activeSite?.name}
          </Typography>
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: 'center',
              color: getCustomTextColor({ hasCustomBranding, customBranding }, theme, Colors.appBar.main),
            }}
          >
            Active since {moment(activeUser?.completed_registration).format('M/DD/YYYY')}
          </Typography>
        </Box>
      </Box>

      <img
        alt="logo"
        style={{
          width: 60,
          height: 60,
          marginLeft: '1rem',
          position: 'absolute',
          bottom: 50,
          zIndex: 50,
        }}
        src={hasCustomBranding && currentSite?.logo_url ? currentSite.logo_url : frusLogo}
      />

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          marginRight: '1rem',
          marginBottom: 'calc(1rem + var(--ion-safe-area-bottom))',
          zIndex: 50,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            marginBottom: '0.5rem',
            color: getCustomTextColor(
              { hasCustomBranding, customBranding },
              theme,
              Colors.primaryColor,
              customTextColorsTypes.button
            ),
          }}
        >
          Food Rescue US
        </Typography>

        <Button
          color="inherit"
          sx={{ display: 'block', marginLeft: 'auto', paddingLeft: '2rem', paddingRight: '2rem' }}
          variant="contained"
          onClick={onExitClick}
        >
          Exit
        </Button>
      </Box>
    </Box>
  );
};

export default IdModeMobileContainer;
